import React, { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import useAuth from '../../hooks/useAuth';
import { useForceChangePasswordMutation } from '../../redux/features/user/usersApiSlice';
import Lock from '../img/Lock.svg';
import eye from '../img/eye.svg';
import eyeclosed from '../img/eyeClosed.svg';
import PasswordValid from '../img/PasswordValid.svg';
import PasswordInvalid from '../img/PasswordInvalid.svg';

const password_regex =
  /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#!@$%.]).{8,}$/;

const passwordInitialValues = {
  newpassword: '',
  confirmnewpassword: '',
};

const passwordValidationSchema = Yup.object({
  newpassword: Yup.string()
    .required('New password is required!')
    .matches(password_regex, 'Weak password.'),
  confirmnewpassword: Yup.string()
    .required('Confirm New password is required!')
    .oneOf([Yup.ref('newpassword')], 'Passwords do not match!'),
});

const ForceChangePasswordModal = () => {
  const userinfo = useAuth();
  const [seeNewPassword, setSeeNewPassword] = useState(false);
  const [seeConfirmPassword, setSeeConfirmPassword] = useState(false);
  const [err, setErr] = useState(null);
  const [forceChangePassword] = useForceChangePasswordMutation();

  const isOpen = !!userinfo?.mustChangePassword;

  const handleSubmit = async (formData) => {
    try {
      await forceChangePassword(formData).unwrap();
      setErr(null);
    } catch (error) {
      setErr(error?.data || 'Could not update password');
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as='div' className='relative z-50' onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='fixed inset-0 bg-black bg-opacity-75' />
        </Transition.Child>

        <div className='fixed inset-0 overflow-y-auto'>
          <div className='flex min-h-full items-center justify-center p-4 text-center'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 scale-95'
              enterTo='opacity-100 scale-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100 scale-100'
              leaveTo='opacity-0 scale-95'
            >
              <Dialog.Panel className='w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all font-manrope'>
                <Dialog.Title
                  as='h3'
                  className='text-lg font-semibold leading-6 text-gray-900'
                >
                  Update your password
                </Dialog.Title>
                <p className='mt-2 text-sm text-gray-500'>
                  You signed in with a temporary password sent by email. Please
                  choose a new password to continue.
                </p>
                <Formik
                  validationSchema={passwordValidationSchema}
                  initialValues={passwordInitialValues}
                  onSubmit={handleSubmit}
                >
                  {({ errors, touched, isValid, dirty, values }) => (
                    <Form className='mt-4'>
                      {errors.newpassword && touched.newpassword ? (
                        <p className='font-semibold mb-2 text-red-500'>
                          {errors.newpassword}
                        </p>
                      ) : (
                        <p className='font-semibold mb-2'>New Password</p>
                      )}
                      <div className='flex'>
                        <span className='bg-[#FBFCFD] inline-flex items-center px-3 text-sm rounded-l-md border border-r-0 border-gray-300'>
                          <img alt='lock' src={Lock} />
                        </span>
                        <Field
                          name='newpassword'
                          type={seeNewPassword ? 'text' : 'password'}
                          className='rounded-none bg-gray-50 border border-l-0 text-gray-900 block flex-1 min-w-0 w-full text-sm border-gray-300 p-2.5'
                          placeholder='New Password'
                        />
                        <span
                          onClick={() => setSeeNewPassword(!seeNewPassword)}
                          className='hover:cursor-pointer inline-flex items-center px-3 bg-[#DBE3EB] rounded-r-md border border-l-0 border-gray-300'
                        >
                          <img
                            alt={seeNewPassword ? 'open eye' : 'closed eye'}
                            src={seeNewPassword ? eye : eyeclosed}
                          />
                        </span>
                      </div>
                      <div className='mt-3 text-sm text-gray-600'>
                        <div className='flex'>
                          <img
                            className='mr-1'
                            alt=''
                            src={
                              values.newpassword.length >= 8
                                ? PasswordValid
                                : PasswordInvalid
                            }
                          />
                          Minimum 8 characters in length.
                        </div>
                        <div className='flex'>
                          <img
                            className='mr-1'
                            alt=''
                            src={
                              /^(?=.*[A-Z])/.test(values.newpassword)
                                ? PasswordValid
                                : PasswordInvalid
                            }
                          />
                          At least 1 uppercase letter.
                        </div>
                        <div className='flex'>
                          <img
                            className='mr-1'
                            alt=''
                            src={
                              /^(?=.*[a-z])/.test(values.newpassword)
                                ? PasswordValid
                                : PasswordInvalid
                            }
                          />
                          At least 1 lowercase letter.
                        </div>
                        <div className='flex'>
                          <img
                            className='mr-1'
                            alt=''
                            src={
                              /^(?=.*[0-9])/.test(values.newpassword)
                                ? PasswordValid
                                : PasswordInvalid
                            }
                          />
                          At least 1 number.
                        </div>
                        <div className='flex'>
                          <img
                            className='mr-1'
                            alt=''
                            src={
                              /^(?=.*?[#!@$%.])/.test(values.newpassword)
                                ? PasswordValid
                                : PasswordInvalid
                            }
                          />
                          At least 1 special character (#!@$%.).
                        </div>
                      </div>
                      {errors.confirmnewpassword &&
                      touched.confirmnewpassword ? (
                        <p className='font-semibold text-red-500 mt-4'>
                          {errors.confirmnewpassword}
                        </p>
                      ) : (
                        <p className='font-semibold mt-4'>
                          Confirm New Password
                        </p>
                      )}
                      <div className='flex mt-2'>
                        <span className='bg-[#FBFCFD] inline-flex items-center px-3 text-sm rounded-l-md border border-r-0 border-gray-300'>
                          <img alt='lock' src={Lock} />
                        </span>
                        <Field
                          name='confirmnewpassword'
                          type={seeConfirmPassword ? 'text' : 'password'}
                          className='rounded-none bg-gray-50 border border-l-0 text-gray-900 block flex-1 min-w-0 w-full text-sm border-gray-300 p-2.5'
                          placeholder='Confirm New Password'
                        />
                        <span
                          onClick={() =>
                            setSeeConfirmPassword(!seeConfirmPassword)
                          }
                          className='hover:cursor-pointer inline-flex items-center px-3 bg-[#DBE3EB] rounded-r-md border border-l-0 border-gray-300'
                        >
                          <img
                            alt={
                              seeConfirmPassword ? 'open eye' : 'closed eye'
                            }
                            src={seeConfirmPassword ? eye : eyeclosed}
                          />
                        </span>
                      </div>
                      {err && (
                        <p className='text-white text-center bg-red-300 p-3 mt-3 rounded-lg'>
                          {err}
                        </p>
                      )}
                      {isValid && dirty ? (
                        <button
                          type='submit'
                          className='mt-6 w-full bg-yellow-gradient btn py-2 px-4 rounded-lg text-center'
                        >
                          Update password
                        </button>
                      ) : (
                        <div className='mt-6 w-full text-white bg-[#B7C6D7] hover:cursor-not-allowed py-2 px-4 rounded-lg text-center'>
                          Update password
                        </div>
                      )}
                    </Form>
                  )}
                </Formik>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ForceChangePasswordModal;
